import { motion } from "framer-motion";
import { MapPin, Clock, Phone, ExternalLink, Instagram } from "lucide-react";
import { HOURS, BRAND } from "../data/menu";

const TRAILER_IMG = "https://customer-assets.emergentagent.com/job_vegan-pizza-cart/artifacts/opf4v86i_WhatsApp%20Image%202026-06-09%20at%2010.39.25%20PM.jpeg";

export default function Locations() {
  return (
    <div data-testid="locations-page" className="pt-32 pb-24">
      <header className="max-w-7xl mx-auto px-6 md:px-10 mb-16">
        <p className="text-[10px] uppercase tracking-[0.3em] text-ember mb-6">Visit our oven</p>
        <h1 className="font-serif text-6xl md:text-8xl leading-[0.9] tracking-tighter text-charred">
          Find <span className="italic text-ember">us</span><br />in Scarborough.
        </h1>
        <p className="mt-8 max-w-xl text-muted2 text-lg leading-relaxed">
          We fire our wood-burning oven from a single home base on Lawrence
          Avenue East. Stop in, watch the dough kiss the flame, and walk out
          with a hot 12&quot; sourdough in hand.
        </p>
      </header>

      {/* Trailer hero shot */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-7xl mx-auto px-6 md:px-10 mb-20"
        data-testid="trailer-hero"
      >
        <div className="aspect-[21/9] md:aspect-[21/8] w-full overflow-hidden bg-charred/5 relative">
          <img
            src={TRAILER_IMG}
            alt="The Old Soul Pizzeria wood-fire trailer parked on Lawrence Avenue East, Scarborough"
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 55%", filter: "saturate(1.05) contrast(1.04)" }}
          />
          <div className="absolute bottom-6 left-6 md:bottom-8 md:left-8 bg-cream px-5 py-3 border border-edge">
            <p className="text-[10px] uppercase tracking-[0.3em] text-ember mb-1">Spot the trailer</p>
            <p className="font-serif text-xl md:text-2xl text-charred leading-tight">Look for the flames on Lawrence Ave E.</p>
          </div>
        </div>
      </motion.section>

      {/* Home base */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 mb-16" data-testid="home-base">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          {/* Address card */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="md:col-span-5"
          >
            <p className="text-[10px] uppercase tracking-[0.3em] text-ember mb-4">Address</p>
            <h2 className="font-serif text-4xl md:text-5xl text-charred leading-[0.95] tracking-tight mb-8">
              1940 Lawrence Ave E.<br /><span className="italic">Scarborough, ON.</span>
            </h2>

            <ul className="space-y-4 text-charred">
              <li>
                <a
                  href={BRAND.mapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="home-base-map"
                  className="inline-flex items-center gap-2 hover:text-ember transition-colors"
                >
                  <MapPin size={16} /> <span className="text-sm tracking-wider">Open in Google Maps</span>
                  <ExternalLink size={12} />
                </a>
              </li>
              <li>
                <a href={BRAND.phoneHref} className="inline-flex items-center gap-2 hover:text-ember transition-colors">
                  <Phone size={16} /> <span className="text-sm tracking-wider">{BRAND.phone}</span>
                </a>
              </li>
              <li>
                <a
                  href={BRAND.instagramUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 hover:text-ember transition-colors"
                >
                  <Instagram size={16} /> <span className="text-sm tracking-wider">{BRAND.instagram}</span>
                </a>
              </li>
            </ul>

            <p className="mt-10 text-sm text-muted2 max-w-sm leading-relaxed">
              Counter service · Walk-ins welcome · Pickup available — call ahead
              to skip the line.
            </p>
          </motion.div>

          {/* Hours table */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="md:col-span-7"
          >
            <div className="flex items-center gap-3 mb-6">
              <Clock size={16} className="text-ember" />
              <p className="text-[10px] uppercase tracking-[0.3em] text-ember">Hours of operation</p>
            </div>
            <div className="border-t border-charred/20">
              {HOURS.map((h) => (
                <div
                  key={h.day}
                  className="flex justify-between py-5 border-b border-edge"
                  data-testid={`hours-${h.day.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="font-serif text-2xl md:text-3xl text-charred">{h.day}</span>
                  <span className="text-sm md:text-base tracking-wider text-muted2 self-center">{h.hours}</span>
                </div>
              ))}
            </div>
            <p className="mt-6 text-xs text-muted2/80 italic">
              Hours may shift on holidays — please check our Instagram for the
              latest updates.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Map embed */}
      <section className="max-w-7xl mx-auto px-6 md:px-10" data-testid="map-section">
        <div className="aspect-[21/9] w-full overflow-hidden bg-edge border border-edge">
          <iframe
            title="Old Soul Pizzeria location"
            src="https://www.google.com/maps?q=1940+Lawrence+Ave+E,+Scarborough,+ON&output=embed"
            className="w-full h-full"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>
    </div>
  );
}
